import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DynamicForm from "../../stories/Forms/DynamicForm";
import { loginFields } from "../../stories/Forms/fields";
import { loginSchema } from "../../stories/Forms/validationSchema";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { BoxArrowInRight, XSquare, InfoCircle } from "react-bootstrap-icons";
import useApis from "../../services/useAPI";
import { loginUrl, sendOtpUrl } from "../../utils/constants";
import { URL_TO_TEST_TYPE, TEST_TYPES } from "../../utils";
import * as yup from "yup";
import Cookies from "js-cookie";
import AccountCard from "../../stories/cards/AccountCard";

let mfa = true;

/**
 * LoginPage Component
 *
 * This component renders the login page for the MyHealthChecked dashboard.
 * It handles user authentication, reCAPTCHA validation, and MFA setup if required.
 *
 * @returns {JSX.Element} The rendered LoginPage component.
 */

const LoginPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { callApi, loading, serverError, resultGet, resultPost } = useApis();
  const [recaptchaRequired, setRecaptchaRequired] = useState(false);
  const params = new URLSearchParams(location.search);
  const emailFromUrl = params.get('email');
  const initialValues = {
    email: emailFromUrl || '',
    password: ''
  };
  const [loginData, setLoginData] = useState(initialValues);
  const { callApi: sendOtpCallApi } = useApis(sendOtpUrl, "POST");

  const isPhlebotomist = location.search.includes("boots") && !!localStorage.getItem("test_type");

  // Extract test_type from URL
  useEffect(() => {
    const pathSegments = location.search.split("%2F");
    let matchedTestType = null;

    pathSegments.forEach((segment) => {
      // Check in URL_TO_TEST_TYPE
      if (URL_TO_TEST_TYPE[segment]) {
        matchedTestType = URL_TO_TEST_TYPE[segment];
      }

      // Check in TEST_TYPES
      if (TEST_TYPES[segment]) {
        matchedTestType = TEST_TYPES[segment];
      }
    });

    if (matchedTestType) {
      localStorage.setItem("test_type", matchedTestType);
    }
  }, [location]);

  // Checks if reCAPTCHA is required
  useEffect(() => {
    const checkReCaptcha = async () => {
      await callApi(null, loginUrl, "GET");
    };

    if(recaptchaRequired){
      checkReCaptcha();
    }
  }, []);

  // Handle GET response to check for reCAPTCHA requirement
  useEffect(() => {
    if (resultGet) {
      setRecaptchaRequired(resultGet.recaptcha_required);
    }
  }, [resultGet]);

  // Shows toast message if there's a server error
  useEffect(() => {
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [serverError]);

  // Handles form submission
  const handleLogin = async (values) => {
    function encodeToBase64(text) {
      // Convert the string to a UTF-8 encoded byte array
      const encoder = new TextEncoder();
      const utf8Bytes = encoder.encode(text);

      // Convert the byte array to a Base64 string
      const base64String = btoa(String.fromCodePoint(...utf8Bytes));

      return base64String;
    }

    const encodedPassword = encodeToBase64(values.password.trim());

    setLoginData({
      email: values?.email,
      password: values.password,
    });

    const payload = {
      email: values.email,
      password: encodedPassword,
      is_phlebotomist: isPhlebotomist,
      ...(recaptchaRequired && { recaptcha_token: values.recaptcha_token }),
    };

    return callApi(payload, loginUrl, "POST");
  };

  // Handle POST response after form submission
  useEffect(() => {
    const url = auth?.nextURl ? `${auth?.nextURl}` : "/";

    if (resultPost?.data) {
      // Check if account is pending/not activated
      if (resultPost.data.state === 'pending' || resultPost.data.message?.includes('not activated')) {
        // Account is pending activation
        auth?.showToast(
          "Account Not Activated",
          resultPost.data.message || "Your account is pending activation. Please verify your email.",
          "../assets/mhc.png",
          <InfoCircle />
        );

        // Navigate to confirm account page
        const email = resultPost.data.email || loginData.email;
        
        // Store email in localStorage or state for the confirm account page
        sessionStorage.setItem('pending_account_email', email);

        // Navigate to confirm account page
        navigate(`/create-account/verify?email=${encodeURIComponent(email)}`, {
          state: { email: email, fromLogin: true },
          replace: true
        });
        return;
      }

      // Normal login flow for active accounts
      // Secure cookie settings for tokens
      if (resultPost.data.access_token) {
        Cookies.set("access_token", resultPost.data.access_token, {
          secure: true, // Only sent over HTTPS
          sameSite: "strict", // Prevents CSRF
          expires: 2 / 24, // 2 hour expiration
        });

        Cookies.set("refresh_token", resultPost.data.refresh_token, {
          secure: true,
          sameSite: "strict",
          expires: 30, // 30 days expiration
        });
      }
      auth.setIsNonUKNumber(resultPost.data.email_mfa);
      const isEmailMFA = resultPost.data.email_mfa;
      const ukPrefixExist = resultPost.data.uk_prefix_exist;
      const shouldGoToOtp =
        resultPost?.data?.mfa_enabled && mfa && (isEmailMFA || ukPrefixExist);

      if (shouldGoToOtp) {
        localStorage.setItem("mfa_pending", true);
        localStorage.removeItem("isLoggedIn");
        auth.setIsAuthenticated(false);
      } else {
        localStorage.removeItem("mfa_pending");
        localStorage.setItem("isLoggedIn", true);
        auth.setIsAuthenticated(true);
      }
      localStorage.setItem("username", resultPost.data.fullname);
      sessionStorage.setItem("email", resultPost.data.email);
      sessionStorage.setItem("email_mfa", resultPost.data.email_mfa);
      localStorage.setItem("role", resultPost.data.role);
      localStorage.setItem("user_id", resultPost.data.id);
      localStorage.setItem(
        "mfa_enabled",
        resultPost.data.mfa_enabled && (isEmailMFA || ukPrefixExist),
      );
      localStorage.setItem("is_phlebotomist", isPhlebotomist);
      localStorage.setItem("is_logged_in_first_time", false);

      const navigationPath = shouldGoToOtp ? "/confirm-otp" : url;

      if (shouldGoToOtp) {
        sendOtpOnLogin(
          resultPost.data.id,
          resultPost.data.access_token,
          resultPost.data.email_mfa,
        );
      }

      navigate(navigationPath, { replace: true });

      // Shows success message if user logs in successfully
      if (!shouldGoToOtp)
        auth?.showToast(
          "Logged in",
          "Logged in successfully!",
          "../assets/mhc.png",
          <BoxArrowInRight />
        );
    } else if (serverError || resultPost?.data?.recaptcha_required) {
      setRecaptchaRequired(true);
    }
  }, [resultPost, navigate, auth, loginData.email]);

  const sendOtpOnLogin = (userId, accessToken, emailMfa) => {
    sendOtpCallApi(
      { user_id: userId, role: "customer", email_mfa: emailMfa },
      null,
      null,
      { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
    );
  };
  // Modify email field to have defaultValue from URL
  const getModifiedFields = () => {
    const modifiedFields = loginFields.slice(0, 2);

    // If email exists in URL, add defaultValue to email field
    if (emailFromUrl && modifiedFields?.[0]?.name === "email") {
      modifiedFields[0] = {
        ...modifiedFields[0],
        defaultValue: emailFromUrl,
        placeholder: "Email address",
        disabled: isPhlebotomist,
      };
    }

    // Add recaptcha field if required
    if (recaptchaRequired) {
      modifiedFields.push({
        controlId: "recaptcha",
        label: "reCAPTCHA",
        type: "recaptcha",
        name: "recaptcha_token",
        required: true,
      });
    }

    // Add submit button
    modifiedFields.push({
      controlId: "3",
      label: "Log in",
      type: "submit",
      href: "/reset-password",
      size: "large",
      variant: "green",
      className: recaptchaRequired ? "btn m-2 sign-in-btn pt-2 w-100" : "btn m-0 sign-in-btn pt-2 w-100",
      ...(recaptchaRequired && {
        links: (
          <div className="create-account-link">
            If you are having trouble logging in, please email{" "}
            <a href="mailto:care@myhealthchecked.com">care@myhealthchecked.com</a>.
          </div>
        ),
      }),
    });

    return modifiedFields;
  };

  const updatedFields = getModifiedFields();

  // Passing schema dynamically based on recaptchaRequired
  const updatedSchema = recaptchaRequired
    ? loginSchema.shape({
      email: yup.string().required("Email is required").email(),
      password: yup.string().required("Password is required"),
      recaptcha_token: yup.string().required("reCAPTCHA is required"),
    })
    : loginSchema;

  return JSON.parse(localStorage.getItem("isLoggedIn")) ? (
    navigate("/")
  ) : isPhlebotomist ? (
      <AccountCard loginPage={false} isPhlebotomist={isPhlebotomist} title="Log in">
        <DynamicForm
          fields={updatedFields}
          validationSchema={updatedSchema}
          initialValues={loginData}
          onSubmit={handleLogin}
          isLoading={loading}
          error={serverError}
        />
      </AccountCard>
  ) : (
    <AccountCard loginPage={true} title="Log in">
      <DynamicForm
        fields={updatedFields}
        validationSchema={updatedSchema}
        initialValues={loginData}
        onSubmit={handleLogin}
        isLoading={loading}
        error={serverError}
        className="login-form-card"
      />
    </AccountCard>
  );
};

export default LoginPage;